import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';
import algoliasearch from 'algoliasearch/lite';

import { MapContainer, TileLayer, ZoomControl, useMap } from 'react-leaflet';
import {contactModal} from './contactModal';
import MarkerLoader from './marker-loader';

import {
    useInstantSearch,
    InstantSearch,
    Highlight,
    Hits,
    Configure,
    SearchBox,
  } from 'react-instantsearch';

const ALGOLIA_APPLICATION_ID = import.meta.env.VITE_ALGOLIA_APPLICATION_ID;
const ALGOLIA_SEARCH_API_KEY = import.meta.env.VITE_ALGOLIA_SEARCH_API_KEY;
const searchClient = algoliasearch(ALGOLIA_APPLICATION_ID, ALGOLIA_SEARCH_API_KEY);

export default () => {
    const root = createRoot(document.getElementById('supportapp'));
    const options = {
        page_name: window.page_name,
        page_title: window.page_title,
        search_index: window.search_index,
        is_arm_search: window.is_arm_search ?? false,
    };
    root.render(<SalesReps options={options}/>);
}


// Display the No Results message when no hits have been returned.
function NoResultsBoundary({ children, fallback }) {
    const { results } = useInstantSearch();
    if (!results.__isArtificial && results.nbHits === 0) {
        return (
            <div className=" bg-brand-blue-50 border border-brand-blue-100/75 p-8 rounded-md flex-1">
                {fallback}
                <div hidden>{children}</div>
            </div>
        );
    }
    return children;
}
  
function NoResults() {
    const { indexUiState } = useInstantSearch();
    return (
      <div className="text-center my-12 mx-auto text-brand-blue-900 text-sm lg:text-base">
        {indexUiState.query !== "" ?? 
            <p>No results for <q>{indexUiState.query}</q>.</p>
        } {
            <div>
                <p>No results could be displayed.</p>
                <p>Try zooming out on the map to see additional nearby results.</p>
            </div>
        }
      </div>
    );
}


function CardServiceAgentEntry(props) {
    const agent = props?.hit;
    return (
        <div key={agent.id} className="agent-card p-4 lg:py-8 border-b flex flex-col">
            <div>
                <h5 className="font-medium text-lg text-brand-blue-900 xl:text-xl/5 mb-6"><Highlight attribute="title" classNames={{highlighted: 'bg-yellow-100'}} hit={agent} /></h5>
                <div className="text-brand-neutral-700 flex flex-col space-y-3">
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 text-sm">

                    { agent.website && 
                        <div className="flex items-center gap-3">
                            <svg className="w-6 lg:w-8 h-auto" width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="0.25" width="32" height="32" rx="16" fill="#ECF3F9"/><path d="M16.722 20.2425L15.7792 21.1853C14.4775 22.4871 12.3669 22.4871 11.0652 21.1853C9.76343 19.8836 9.76343 17.773 11.0652 16.4713L12.008 15.5285M20.4933 16.4713L21.4361 15.5285C22.7378 14.2267 22.7378 12.1162 21.4361 10.8144C20.1343 9.5127 18.0238 9.5127 16.722 10.8144L15.7792 11.7573M13.9173 18.3332L18.584 13.6665" stroke="#2678AB" strokeWidth="0.75" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            <a
                                className='truncate hover:text-brand-blue-500'
                                href={agent.website}
                                target="_blank"
                                onClick={() => {
                                    gtag('event', 'service_agent', {
                                        'company_name': `${agent.company}`,
                                        'state': `${agent.state}`,
                                        'event_type': 'web'
                                    });
                                }}>{agent.website}</a>
                        </div>
                    }

                    { agent.city && 
                        <div className="flex items-center gap-3">
                            <svg className="w-6 lg:w-8 h-auto" width="32px" height="32px" viewBox="0 0 32 32" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink"><g id="Page-1" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd"><g id="Group"><rect id="Rectangle" fill="#ECF3F9" fillRule="nonzero" x="0" y="0" width="32" height="32" rx="16"></rect><path d="M16.0003,16.6666 C17.1049,16.6666 18.0003,15.7712 18.0003,14.6666 C18.0003,13.562 17.1049,12.6666 16.0003,12.6666 C14.8958,12.6666 14.0003,13.562 14.0003,14.6666 C14.0003,15.7712 14.8958,16.6666 16.0003,16.6666 Z" id="Path" stroke="#2678AB" strokeWidth="0.75" strokeLinecap="round" strokeLinejoin="round"></path><path d="M16.0003,22.6666 C18.667,19.9999 21.3337,17.6121 21.3337,14.6666 C21.3337,11.7211 18.9458,9.33325 16.0003,9.33325 C13.0548,9.33325 10.667,11.7211 10.667,14.6666 C10.667,17.6121 13.3337,19.9999 16.0003,22.6666 Z" id="Path" stroke="#2678AB" strokeWidth="0.75" strokeLinecap="round" strokeLinejoin="round"></path></g></g></svg>
                            <div className='flex flex-col'>
                                <div>{agent.city}, {agent.state}</div>
                            </div>
                        </div>
                    }

                    { agent.phone && 
                        <div className="flex items-center gap-3">
                            <svg className="w-6 lg:w-8 h-auto" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><g fill="none" fillRule="evenodd"><rect width="32" height="32" fill="#ECF3F9" fillRule="nonzero" rx="16"/><path stroke="#2678AB" strokeLinecap="round" strokeLinejoin="round" strokeWidth=".75" d="M13.587 13.902a9.735 9.735 0 0 0 1.897 2.674 9.735 9.735 0 0 0 2.674 1.897c.083.04.124.06.177.075a.697.697 0 0 0 .575-.098c.044-.032.082-.07.159-.146.233-.233.35-.35.466-.426a1.333 1.333 0 0 1 1.454 0c.117.076.234.193.467.426l.13.13c.354.354.531.531.627.722.192.378.192.825 0 1.203-.096.19-.273.368-.627.722l-.105.105c-.354.353-.53.53-.77.665-.266.15-.68.257-.986.256-.275-.001-.463-.055-.84-.161a12.691 12.691 0 0 1-5.522-3.25 12.691 12.691 0 0 1-3.249-5.522c-.107-.376-.16-.564-.16-.84a2.242 2.242 0 0 1 .255-.985c.135-.24.312-.417.665-.77l.105-.105c.354-.354.531-.531.722-.627a1.333 1.333 0 0 1 1.203 0c.19.096.368.273.722.627l.13.13c.233.233.35.35.426.467a1.333 1.333 0 0 1 0 1.454c-.077.117-.193.233-.426.466a1.336 1.336 0 0 0-.146.16.697.697 0 0 0-.098.574c.015.053.035.094.075.177Z"/></g></svg>
                            <a
                                className='truncate hover:text-brand-blue-500'
                                href={`tel:${agent.phone.trim()}`}
                                onClick={() => {
                                    gtag('event', 'service_agent', {
                                        'company_name': `${agent.company}`,
                                        'state': `${agent.state}`,
                                        'event_type': 'telephone'
                                    });
                                }}>{agent.phone.trim()}</a>
                        </div>
                    }
                    
                    { agent.email && 
                        <div className="flex items-center gap-3">
                            <svg className="w-6 lg:w-8 h-auto" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><g fill="none" fillRule="evenodd"><rect width="32" height="32" fill="#ECF3F9" fillRule="nonzero" rx="16"/><path stroke="#2678AB" strokeLinecap="round" strokeLinejoin="round" strokeWidth=".75" d="m22.333 20-4.429-4m-3.81 0-4.428 4m-.333-7.333 5.443 3.81c.441.309.662.463.901.523.212.052.433.052.645 0 .24-.06.46-.214.901-.523l5.443-3.81m-10.133 8.666h6.933c1.12 0 1.68 0 2.108-.218a2 2 0 0 0 .874-.874c.218-.427.218-.988.218-2.108v-4.266c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874c-.428-.218-.988-.218-2.108-.218h-6.933c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874c-.218.428-.218.988-.218 2.108v4.266c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874c.428.218.988.218 2.108.218Z"/></g></svg>
                            <a
                                className='truncate hover:text-brand-blue-500'
                                href={`mailto:${agent.email.trim()}`}
                                onClick={() => {
                                    gtag('event', 'service_agent', {
                                        'company_name': `${agent.company}`,
                                        'state': `${agent.state}`,
                                        'event_type': 'email'
                                    });
                                }}
                                >
                                {agent.email.trim()}
                                </a>
                        </div>
                    }
                    </div>
                </div>
            </div>
        </div>
    )
}


function CardSalesRepEntry(props) {
    const rep = props?.hit;
    return (
        <div key={rep.id} className="agent-card py-4 sm:py-6 md:py-8 border-b flex flex-col">
            <div>
                <h5 className="font-medium text-lg text-brand-blue-900 xl:text-xl/5 mb-2 w-full md:flex lg:block xl:flex justify-between items-center">
                    <Highlight attribute="state" classNames={{highlighted: 'bg-yellow-100'}} hit={rep} />
                    <div className="text-sm xl:text-base">{rep.company}</div>
                </h5>
                { rep.area && 
                    <div className="text-sm text-brand-neutral-700 mb-2 lg:mb-4 lg:max-w-[60%]">{rep.area}</div>
                }
                <div className="flex flex-col sm:flex-row gap-2 pt-2 lg:pt-0 justify-between">
                { rep.email && 
                    <div className="flex items-center gap-3">
                        <svg className="w-6 lg:w-8 h-auto" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><g fill="none" fillRule="evenodd"><rect width="32" height="32" fill="#ECF3F9" fillRule="nonzero" rx="16"></rect><path stroke="#2678AB" strokeLinecap="round" strokeLinejoin="round" strokeWidth=".75" d="m22.333 20-4.429-4m-3.81 0-4.428 4m-.333-7.333 5.443 3.81c.441.309.662.463.901.523.212.052.433.052.645 0 .24-.06.46-.214.901-.523l5.443-3.81m-10.133 8.666h6.933c1.12 0 1.68 0 2.108-.218a2 2 0 0 0 .874-.874c.218-.427.218-.988.218-2.108v-4.266c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874c-.428-.218-.988-.218-2.108-.218h-6.933c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874c-.218.428-.218.988-.218 2.108v4.266c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874c.428.218.988.218 2.108.218Z"></path></g></svg>
                        <a className='text-brand-blue-300 inline-modal' href="#" onClick={() => { contactModal(rep) }}>Contact Sales Rep</a>
                    </div>
                }
                 { rep.phone && 
                    <div className="flex items-center gap-3">
                        <svg className="w-6 lg:w-8 h-auto" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><g fill="none" fillRule="evenodd"><rect width="32" height="32" fill="#ECF3F9" fillRule="nonzero" rx="16"></rect><path stroke="#2678AB" strokeLinecap="round" strokeLinejoin="round" strokeWidth=".75" d="M13.587 13.902a9.735 9.735 0 0 0 1.897 2.674 9.735 9.735 0 0 0 2.674 1.897c.083.04.124.06.177.075a.697.697 0 0 0 .575-.098c.044-.032.082-.07.159-.146.233-.233.35-.35.466-.426a1.333 1.333 0 0 1 1.454 0c.117.076.234.193.467.426l.13.13c.354.354.531.531.627.722.192.378.192.825 0 1.203-.096.19-.273.368-.627.722l-.105.105c-.354.353-.53.53-.77.665-.266.15-.68.257-.986.256-.275-.001-.463-.055-.84-.161a12.691 12.691 0 0 1-5.522-3.25 12.691 12.691 0 0 1-3.249-5.522c-.107-.376-.16-.564-.16-.84a2.242 2.242 0 0 1 .255-.985c.135-.24.312-.417.665-.77l.105-.105c.354-.354.531-.531.722-.627a1.333 1.333 0 0 1 1.203 0c.19.096.368.273.722.627l.13.13c.233.233.35.35.426.467a1.333 1.333 0 0 1 0 1.454c-.077.117-.193.233-.426.466a1.336 1.336 0 0 0-.146.16.697.697 0 0 0-.098.574c.015.053.035.094.075.177Z"></path></g></svg>
                        <a className='text-brand-blue-300'
                            href={`tel:${rep.phone.trim()}`}
                            onClick={() => {
                                gtag('event', 'sales_rep', {
                                    'company_name': `${rep.company}`,
                                    'state': `${rep.state}`,
                                    'event_type': 'telephone'
                                });
                            }}
                            >{rep.phone}</a>
                    </div>
                }
                </div> 
            </div>
        </div>
    )
}


function SalesReps({options}) {



        const isServiceAgent = options.search_index === 'service_agents';
        const componentName = isServiceAgent ? CardServiceAgentEntry : CardSalesRepEntry;
        const markerComponentName = options.search_index;
        const configureFilters = options.is_arm_search ? 'is_arm:true' : '';

        const [data, setData] = useState(false);
        const [defaultCenter, setDefaultCenter] = useState([39.828175,-98.5795]);
        const [defaultZoom, setDefaultZoom] = useState(3.5);
        const [defaultTab, setActiveTab] = useState(0);
        const [didChange, setDidChange] = useState(false);

        
        const ResetController = (childdata) => {
            setData(childdata);
            setDidChange(true);
            setDefaultCenter([39.828175,-98.5795]);
            setDefaultZoom(4);
        }

        const TabStateController = (childdata) => {

            let currentTab = defaultTab;
            setActiveTab(childdata);
            setDidChange(false);

            let mapDiv = document.getElementById("hits-map");
            let listDiv = document.getElementById("hits-list");
            if (mapDiv && childdata !== currentTab) {            

                // switch to map view
                if (defaultTab === 0) {
                    mapDiv.classList.add('on');
                    mapDiv.classList.remove('off');
                    listDiv.classList.add('off');
                }
                // switch to list view
                if (defaultTab === 1) {
                    listDiv.classList.add('on');
                    listDiv.classList.remove('off');
                    mapDiv.classList.add('off');
                }
            }
        }

        function MapController() {
            const map = useMap();
            if (didChange && data) {
                map.setView(defaultCenter, defaultZoom);
            }
            return null
        }

        function Tabs () {

            let inActiveClass = 'bg-brand-blue-50 text-brand-blue-900';
            let isActiveClass = 'bg-brand-blue-300 text-white';
            let ListActive = defaultTab === 0 ? isActiveClass : inActiveClass;
            let MapActive = defaultTab === 1 ? isActiveClass : inActiveClass;
    
            return <div className="flex items-center lg:hidden gap-2 mt-4" id="mapTabs">
                <div onClick={() => TabStateController(0)}  className={'rounded-full text-sm py-3 px-6 flex items-center border border-brand-neutral-300 has-icon cursor-pointer ' + ListActive}>
                    <svg className={defaultTab === 0 ? 'w-4 h-auto text-brand-blue-50' : 'w-4 h-auto text-brand-blue-300'} width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14 8.23698L6 8.23698M14 4.23698L6 4.23698M14 12.237L6 12.237M3.33333 8.23698C3.33333 8.60517 3.03486 8.90365 2.66667 8.90365C2.29848 8.90365 2 8.60517 2 8.23698C2 7.86879 2.29848 7.57031 2.66667 7.57031C3.03486 7.57031 3.33333 7.86879 3.33333 8.23698ZM3.33333 4.23698C3.33333 4.60517 3.03486 4.90365 2.66667 4.90365C2.29848 4.90365 2 4.60517 2 4.23698C2 3.86879 2.29848 3.57031 2.66667 3.57031C3.03486 3.57031 3.33333 3.86879 3.33333 4.23698ZM3.33333 12.237C3.33333 12.6052 3.03486 12.9036 2.66667 12.9036C2.29848 12.9036 2 12.6052 2 12.237C2 11.8688 2.29848 11.5703 2.66667 11.5703C3.03486 11.5703 3.33333 11.8688 3.33333 12.237Z" stroke="currentColor" strokeWidth="0.75"  strokeLinejoin="round"/></svg>
                    List
                </div>
                <div onClick={() => TabStateController(1)}  className={'rounded-full text-sm py-3 px-6 flex items-center border border-brand-neutral-300 has-icon cursor-pointer ' + MapActive}>
                    <svg className={defaultTab === 1 ? 'w-4 h-auto text-brand-blue-50' : 'w-4 h-auto text-brand-blue-300'} width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.9974 8.90365C9.10196 8.90365 9.9974 8.00822 9.9974 6.90365C9.9974 5.79908 9.10196 4.90365 7.9974 4.90365C6.89283 4.90365 5.9974 5.79908 5.9974 6.90365C5.9974 8.00822 6.89283 8.90365 7.9974 8.90365Z" stroke="currentColor" strokeWidth="0.75"  strokeLinejoin="round"/><path d="M7.9974 14.9036C10.6641 12.237 13.3307 9.84916 13.3307 6.90365C13.3307 3.95813 10.9429 1.57031 7.9974 1.57031C5.05188 1.57031 2.66406 3.95813 2.66406 6.90365C2.66406 9.84916 5.33073 12.237 7.9974 14.9036Z" stroke="currentColor" strokeWidth="0.75"  strokeLinejoin="round"/></svg>
                    Map
                </div>
                <div className="ml-auto mr-2">
                    <a href="#" onClick={() => ResetController(true)} className="rounded-full w-8 h-8 hover:bg-brand-neutral-200 flex items-center justify-center">
                        <svg className="text-brand-blue-300 w-4 h-auto" xmlns="http://www.w3.org/2000/svg" width="36" height="36" fill="currentColor" viewBox="0 0 256 256"><path d="M224,128a96,96,0,0,1-94.71,96H128A95.38,95.38,0,0,1,62.1,197.8a8,8,0,0,1,11-11.63A80,80,0,1,0,71.43,71.39a3.07,3.07,0,0,1-.26.25L44.59,96H72a8,8,0,0,1,0,16H24a8,8,0,0,1-8-8V56a8,8,0,0,1,16,0V85.8L60.25,60A96,96,0,0,1,224,128Z"></path></svg>
                    </a>
                </div>
            </div>
        }

        return (
            <div className="container mx-auto flex flex-col lg:min-h-[75vh] relative z-10">
                <div className="lg:flex flex-1 gap-8">
                
                    <InstantSearch
                        searchClient={searchClient}
                        indexName={options.search_index}
                    >
                    <Configure hitsPerPage={100} filters={configureFilters}  />

                    <div className="lg:w-2/5 pt-8 md:pt-12">
                        <h1 className='leading-tight font-medium mb-4 lg:mb-6 xl:mb-8 text-2xl md:text-3xl lg:text-4xl text-brand-blue-900'>{options.page_title}</h1>
                        <SearchBox
                            placeholder="Search by state or name"
                            classNames={{
                                root: 'w-full mx-auto',
                                form: 'relative',
                                input: 'w-full border border-brand-neutral-400 outline-brand-blue-100 rounded-md p-4 pl-12',
                                submit: 'absolute left-4 top-4',
                                reset: 'absolute right-4 top-4'
                            }}
                            submitIconComponent={() => (
                                <svg className="w-6 h-auto fill-current text-slate-400" xmlns="http://www.w3.org/2000/svg" width="36" height="36" fill="#000000" viewBox="0 0 256 256"><path d="M229.66,218.34l-50.07-50.06a88.11,88.11,0,1,0-11.31,11.31l50.06,50.07a8,8,0,0,0,11.32-11.32ZM40,112a72,72,0,1,1,72,72A72.08,72.08,0,0,1,40,112Z"></path></svg>
                            )}
                            resetIconComponent={({ classNames }) => (
                                <svg className="w-6 h-auto fill-current text-slate-600" xmlns="http://www.w3.org/2000/svg" width="36" height="36" fill="#000000" viewBox="0 0 256 256"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path></svg>
                            )}                                    
                        />
                        <Tabs/>
                        <div className="border-t border-brand-neutral-300 pt-4 mt-4 relative" id="horizontal-bar"></div>
                        <div id="hits-list">
                            <NoResultsBoundary fallback={<NoResults />}>
                                <Hits hitComponent={componentName} classNames={{root: 'flex-1', list: 'grid grid-cols-1 max-h-[800px] lg:max-h-[60vh] overflow-auto pr-4'}}/>
                            </NoResultsBoundary>
                        </div>
                    </div>
                    <div className="lg:w-3/5 flex">
                        <MapContainer
                            className="w-full flex-1 aspect-[9/12]"
                            id="hits-map"
                            center={defaultCenter}
                            zoom={defaultZoom}
                            zoomControl={false}
                            minZoom={3}
                            scrollWheelZoom={false}
                            doubleClickZoom={true}
                            attributionControl={false}
                        >
                            <MapController/>
                            <ZoomControl position={'topright'}/>
                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>
                            <MarkerLoader tag={markerComponentName} />
                        </MapContainer>
                    </div>
                </InstantSearch>
                </div>
            </div>
    );
}